import { useEffect, useState } from 'react'
import { useRouter } from 'next/router'
import dynamic from 'next/dynamic'
import {
  Button,
  Column,
  Container,
  MobileModalWrapper,
  MoreDetailBox,
  Spacer
} from '@smuchile/pkg-unimarc-components'
import {
  BigScreen,
  getGlobalStyle,
  SmallScreen
} from '@smuchile/pkg-unimarc-components/helpers'
import { useFilterCategories, useFilter, useOrder } from 'shared/utils/hooks'
import {
  ContentfulResponse,
  generateBreadcrumbJoin,
  IContentfulBasicEntry,
  IContentfulResponse,
  isValidArrayWithData,
  useAndesML,
  useCatalogFacets,
  useCategories,
  useContentful,
  usePagination,
  useProductsSearch
} from '@smuchile/pkg-unimarc-hooks'
import { useBannerSearch } from 'shared/hooks/useBannerSearch'
import { adaptBannerToContentfulFormat } from 'shared/helpers/bannerAdapter'
import {
  categoriesForBreadcrumbs,
  changeUrlCategories,
  contentBannerLegal,
  getQueryKeyToRequest,
  isContinuosFetching,
  matchOptionKey,
  orderByOptions,
  paginationCal,
  getInitialFilters
} from 'shared/helpers'
import {
  BodyPageProducts,
  BodyPageProductsProps,
  ErrorPageV2Props,
  LegalTerms,
  PlpDescription
} from '..'
import { MonetiseBannerPLP } from './helpers'
import { REACT_QUERY_GENERAL } from 'shared/constants/reactQuery'
import { FACETS_AND_ORDERS_BY_FILTERS } from 'shared/utils/constanst'
import { FacetsAndOrderByFiltersContent } from 'components/FacetsAndOrderByFiltersContent'

const ErrorPage = dynamic<ErrorPageV2Props>(
  import('..').then((mod) => {
    return mod.ErrorPageV2
  })
)

interface ProductListPageProps {
  categories: Array<string>
  categoriesId?: string
  firstLoad?: number
  isMobile?: boolean
  page: number
  seoDetailBox?: IContentfulBasicEntry
  handleFirstLoad?: () => void
}

const initialLimit = 49

const customSpacer = ({ isMobile }) => {
  const spacerSize: typeof Spacer.arguments.size = isMobile ? 8 : 32

  return { spacerSize }
}

export const ProductListPage = ({
  categories,
  categoriesId,
  firstLoad,
  isMobile,
  seoDetailBox,
  handleFirstLoad
}: ProductListPageProps) => {
  const [showFiltersModal, setShowFiltersModal] = useState(false)
  const [categoriesTree, setCategoriesTree] = useState(categoriesId)
  const [selectedTab, setSelectedTab] = useState(0)
  const router = useRouter()
  const {
    applyFilters,
    compareAndSetInitialData,
    currentSelectedFilters,
    filterData
  } = useFilter(router) || {}

  const { handleClickCategoryPill, place, treeValue } = useFilterCategories(
    router,
    categoriesId
  )

  const {
    selectedRadio,
    disabledButton,
    onClickFilter,
    onClickRadioOrderByOption,
    onApplyFilterModal
  } = useOrder(router, setShowFiltersModal)

  const page = parseInt(router?.query?.page as string) || 1
  const { offset, end: limit } = paginationCal({ page, limit: 50 })
  const nextLimit = offset + initialLimit
  const [resources, setResources] = useState(Infinity)
  const [offSet, setOffSet] = useState(offset)
  const [countLimit, setCountLimit] = useState(offSet + initialLimit)
  const [products, setProducts] = useState([])
  const orderByFilterName = matchOptionKey(
    router?.query?.orderBy?.toString() ?? ''
  )
  const { spacerSize } = customSpacer({ isMobile })

  // get all categories menu like with getCategories service
  const resultAisles = useCategories({
    level: 2,
    reactQuery: REACT_QUERY_GENERAL
  })
  // get a clean object of categories and return breadcrumbs and pills
  const {
    breadcrumbData,
    subCategories,
    title,
    categoryId = ''
  } = categoriesForBreadcrumbs({
    router,
    categories,
    resultAisles
  })

  const dataBreadCrumb = changeUrlCategories(breadcrumbData)
  const reactQueryByCategory = {
    ...REACT_QUERY_GENERAL,
    enabled: Boolean(categoryId) && offSet < countLimit
  }
  const keysToQueryCFbanner = getQueryKeyToRequest([
    {
      value: categoryId
    },
    {
      value: 'plpBannerLegal'
    },
    {
      value: categoriesId
    }
  ])
  // get data to banner legal
  const { data: bannerSearchData } = useBannerSearch({
    type: 'category',
    value: categoriesId,
    enabled: Boolean(categoriesId) && offSet < countLimit,
    reactQuery: { ...reactQueryByCategory, queryKey: keysToQueryCFbanner }
  })

  const contentCategoriesData = adaptBannerToContentfulFormat(bannerSearchData)

  const contentSeoPlp = useContentful({
    options: {
      'fields.platform': 'Web Unimarc eComm',
      'fields.referenceId': categoryId,
      'fields.referenceType': 'category',
      content_type: 'landingpagesParaLaWeb'
    },
    reactQuery: { ...reactQueryByCategory, staleTime: 0 }
  })

  const seoPlpData = contentSeoPlp.data
    ? new ContentfulResponse(contentSeoPlp.data as IContentfulResponse)
        .populateEntries()
        .getResponse()
    : []

  const initialFilters = getInitialFilters(router?.query)
  const keysToQuery = getQueryKeyToRequest([
    ...initialFilters,
    {
      value: {
        ...currentSelectedFilters,
        ...applyFilters
      }
    },
    { value: categoriesTree },
    { value: offset.toString() },
    { value: countLimit.toString() },
    { value: orderByFilterName }
  ])

  const { bannerImages, contentLegalParagraph, contentLegalTitle } =
    contentBannerLegal(contentCategoriesData)

  const responseFacetsData = useCatalogFacets({
    ...currentSelectedFilters,
    ...applyFilters,
    categories: categoriesTree,
    reactQuery: {
      ...REACT_QUERY_GENERAL,
      queryKey: keysToQuery
    }
  })

  const productsSearch = useProductsSearch({
    ...currentSelectedFilters,
    ...applyFilters,
    categories: categoriesTree,
    from: offSet.toString(),
    to: countLimit.toString(),
    orderBy: orderByFilterName,
    reactQuery: {
      ...reactQueryByCategory,
      queryKey: keysToQuery,
      enabled: categoriesTree?.length !== 0
    }
  })
  const bannerAndesMl = useAndesML(
    {
      categoryName: categoriesTree,
      deviceType: isMobile ? 'MOBILE' : 'DESKTOP',
      pageType: 'PLP',
      requestPage: `${process.env.NEXT_PUBLIC_HOMEURL}${router.asPath}`,
      slot: 'TOP BANNERS'
    },
    {
      queryKey: ['getAndesMl', categoriesTree],
      ...REACT_QUERY_GENERAL
    }
  )
  const bannerAndesMLData = bannerAndesMl?.data?.[0] || {}
  const isLoadingFacets =
    responseFacetsData?.isLoading || responseFacetsData?.isFetching
  const urlLinkCategory = contentCategoriesData?.items?.[0]?.fields?.urlRedirect
  const continuosFetching: boolean = isContinuosFetching({
    countLimit,
    resources,
    limit
  })
  const data = contentCategoriesData?.items?.[0]?.fields
  const bannerDataInfo =
    Object.entries(bannerAndesMLData)?.length > 0 ? bannerAndesMLData : data
  const facetsFilterV2 = responseFacetsData?.data || {}
  const isLoadingPage =
    resultAisles.isLoading ||
    productsSearch.isLoading ||
    productsSearch.isFetching
  const propsNavigation = usePagination({
    totalQuantity: resources,
    itemPerPage: 50,
    defaultPage: page,
    handleQuantity: () => {
      const newCountLimit = nextLimit > limit ? limit : nextLimit
      setOffSet(offset)
      setCountLimit(newCountLimit)
    }
  })

  const handleFetchProducts = () => {
    if (!productsSearch.isLoading && continuosFetching) {
      setOffSet(countLimit + 1)
      setCountLimit((prev) => {
        const defaultLimit = limit > resources ? resources : limit
        const nextLimit = prev + initialLimit + 1
        return nextLimit > limit || nextLimit > resources
          ? defaultLimit
          : nextLimit
      })
    }
  }

  const isLoadingPageAndFacets = isLoadingFacets || isLoadingPage
  const handleToggleFiltersModal = () => {
    setShowFiltersModal(!showFiltersModal)
  }

  const dataOfBodyPageProducts: BodyPageProductsProps = {
    amountOfProducts: products?.length,
    bannerAndesMl: bannerDataInfo,
    bannerImages,
    breadcrumbData: dataBreadCrumb,
    continuosFetching,
    facetsFilterV2,
    filterData: { ...filterData, isLoadingFacets: isLoadingPageAndFacets },
    firstLoad,
    hasFacets: filterData.hasFilter,
    imageAndes: bannerAndesMLData?.imageUrl,
    isLoadingBannerAndes: bannerAndesMl.isLoading,
    isMobile,
    key: categoriesId,
    place,
    products,
    propsNavigation,
    resultAisles: resultAisles,
    selectedRadio,
    subCategories,
    title,
    totalQuantity: resources,
    urlLink: bannerAndesMLData?.redirectUrl || urlLinkCategory,
    handleFirstLoad,
    handleOnSelectRange: filterData.handleOnSelectRange,
    handleOnTogglePromotions: filterData.handleOnTogglePromotions,
    handleOpenFiltersModal: setShowFiltersModal,
    onClickCategoryPill: handleClickCategoryPill,
    onClickFilter,
    onClickRadioOrderByOption,
    setCountLimit: handleFetchProducts
  }

  useEffect(() => {
    compareAndSetInitialData(facetsFilterV2)
  }, [facetsFilterV2])

  useEffect(() => {
    const {
      availableProducts = [],
      notAvailableProducts = [],
      resource: count
    } = productsSearch?.data || {}

    if (
      !Array.isArray(availableProducts) ||
      !Array.isArray(notAvailableProducts)
    ) {
      // eslint-disable-next-line no-console
      console.error('Invalid products data:', productsSearch?.data)
      return
    }

    const newProducts = [...availableProducts, ...notAvailableProducts]

    if (count && parseInt(count) !== resources) {
      setResources(parseInt(count))
    }

    setProducts((prev) => {
      const prevData = continuosFetching ? prev : []

      if (isLoadingPage && continuosFetching) {
        // eslint-disable-next-line no-console
        console.log('Skipping product update due to loading state')
        return prev
      }

      return [...prevData, ...newProducts]
    })
  }, [
    productsSearch?.data,
    isLoadingPage,
    countLimit,
    continuosFetching,
    resources
  ])

  useEffect(() => {
    if (router?.query?.categories?.length === 1) {
      setCategoriesTree(categoriesId)
    } else if (router?.query?.categories?.length > 1) {
      if (!treeValue) {
        setCategoriesTree(treeValue)
      } else {
        setCategoriesTree(categoriesId)
      }
    }
  }, [router?.query?.categories])

  useEffect(() => {
    propsNavigation.setPage(page)
  }, [categories, page])

  useEffect(() => {
    generateBreadcrumbJoin(dataBreadCrumb)
  }, [dataBreadCrumb])

  useEffect(() => {
    if (!router?.query?.orderBy) {
      onClickRadioOrderByOption('Recomendados')
    }
  }, [router?.query?.orderBy])

  // when service in client-side response with error property
  if (
    !isLoadingPage &&
    (productsSearch?.isError ||
      (productsSearch?.error && !productsSearch?.data?.resource))
  ) {
    return <ErrorPage errorType='400' />
  }

  // when service in client-side response a 404 or no data
  if (
    !isLoadingPage &&
    productsSearch?.error &&
    !isValidArrayWithData(productsSearch?.data?.availableProducts)
  ) {
    return <ErrorPage errorType='404' />
  }

  return (
    <>
      <Container
        alignItems='start'
        className='productListPage__main'
        justifyContent='center'
        tagName='main'
      >
        <BigScreen>
          <Column alignItems='center'>
            {!bannerAndesMl.isLoading && (
              <MonetiseBannerPLP
                data={bannerDataInfo}
                imageAndes={bannerAndesMLData?.imageUrl}
                images={bannerImages}
                isLoadingPage={isLoadingPage}
                key={categoriesId}
                padding='32px 0 0 0'
                resultAisles={resultAisles}
                urlLink={bannerAndesMLData?.redirectUrl || urlLinkCategory}
              />
            )}
            <Container
              backgroundColor={getGlobalStyle('--color-background-white')}
              isWrap
              maxWidth={getGlobalStyle('--width-max-desktop')}
              padding='10px 0px'
              width='1120px'
            >
              <Column alignItems='center'>
                <BodyPageProducts
                  key={`PLP-${categories}`}
                  {...dataOfBodyPageProducts}
                />
              </Column>
            </Container>
            <LegalTerms
              paragraph={contentLegalParagraph}
              title={contentLegalTitle}
            />
          </Column>
        </BigScreen>

        <SmallScreen>
          <Column backgroundColor={getGlobalStyle('--color-base-white')}>
            {showFiltersModal && (
              <MobileModalWrapper
                blockId='facetsAndOrderByFilters'
                body={
                  <FacetsAndOrderByFiltersContent
                    amountOfProducts={products?.length}
                    facetFilterProps={filterData}
                    isMobile={isMobile}
                    onClickDetectTab={setSelectedTab}
                    orderByProps={{
                      onClick: onClickRadioOrderByOption,
                      options: orderByOptions,
                      selected: selectedRadio
                    }}
                  />
                }
                headerLeftElement={
                  selectedTab === 0 &&
                  filterData.hasFilter && (
                    <Column
                      flexGrow={0}
                      width='auto'
                    >
                      <Button
                        color={getGlobalStyle('--color-primary-light-red')}
                        fontSize='14px'
                        fontWeight='600'
                        height='initial'
                        label='Limpiar'
                        minWidth='auto'
                        onClick={filterData.handleClearOnClick}
                        type='plain'
                      />
                    </Column>
                  )
                }
                headerTitle={FACETS_AND_ORDERS_BY_FILTERS.TITLE}
                iconSize={20}
                isEnableButton={!disabledButton || filterData?.enableButton}
                modalConfigsProps={{
                  toggle: handleToggleFiltersModal,
                  isOpen: showFiltersModal,
                  toggleOutside: handleToggleFiltersModal
                }}
                onApply={() => {
                  if (selectedTab === 1) return onApplyFilterModal()
                  if (selectedTab === 0)
                    return filterData.handleApplyOnClick(
                      handleToggleFiltersModal
                    )
                  return null
                }}
                onClose={handleToggleFiltersModal}
              />
            )}
            {!isMobile && (bannerAndesMl || bannerImages) && (
              <Spacer.Horizontal size={spacerSize} />
            )}
            <BodyPageProducts {...dataOfBodyPageProducts} />
            <LegalTerms
              paragraph={contentLegalParagraph}
              title={contentLegalTitle}
            />
          </Column>
        </SmallScreen>
      </Container>
      {isValidArrayWithData(seoPlpData?.['items']) && (
        <PlpDescription
          isMobile={isMobile}
          items={seoPlpData['items']}
        />
      )}
      {isValidArrayWithData(
        (seoDetailBox?.fields?.text as { content: [] })?.content
      ) && (
        <Container
          margin={isMobile ? '0' : '21px 0'}
          padding={isMobile ? '0 16px' : '0'}
        >
          <MoreDetailBox
            backgroundColor={seoDetailBox?.fields?.backgroundColor as string}
            fontColor={seoDetailBox?.fields?.fontColor as string}
            isMobile={isMobile}
            linksColor={seoDetailBox?.fields?.linksColor as string}
            richTextData={[
              (seoDetailBox?.fields?.text as { content: [] })?.content
            ]}
            title={seoDetailBox?.fields?.title as string}
          />
        </Container>
      )}
    </>
  )
}
